import {
  GroupData,
  LanguageNameSpaces,
  PlatformType,
  WorkspaceType,
} from "@shared/types";
import Card from "common/Boxes/Card";
import Flex from "common/Boxes/Flex";
import {
  CrowLogo,
  Discord,
  PrivateIcon,
  PublicIcon,
  Telegram,
} from "common/Icons/Icons";
import Title from "common/Typography/Title";
import React, { ReactNode, useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import { getPanelShadow } from "common/NewLook/tokens.ts";
import GroupMainInfo from "features/newMarketplace/common/GroupMainInfo";
import {
  MOBILE_ONBOARDING_GROUP_CARD_WIDTH,
  ONBOARDING_GROUP_CARD_WIDTH,
  ONBOARDING_MY_GROUP_CARD_HEIGHT,
} from "@shared/features/onboarding/constants";
import { MOBILE_VIRTUAL_LIST } from "common/constants";
import { GroupsPageContext } from "@shared/features/groups/GroupsPage.context";
import { getGroupSubscriptionExpiration } from "@shared/utils/misc";
import Text from "common/Typography/Text";
import { INVALID_GROUP_AUTO_TRADING_WARNING } from "@shared/features/configuration/constants";
import Tooltip from "common/Tooltips/Tooltip";
import MyGroupActions from "features/onboarding/methodSettings/signalsGroup/groupCards/MyGroupActions.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useSelector } from "react-redux";
import { getInactiveGroupAutoTradingWarning } from "@shared/features/configuration/utils.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import { GROUP_CARD_ID } from "testIds.ts";

interface MyGroupCardProps {
  group: GroupData;
  onCardClick: () => void;
}

const MyGroupCard = ({ group, onCardClick }: MyGroupCardProps) => {
  const theme = useTheme();
  const { isWhitelabel } = useBranding();
  const { isGroupsPage } = useContext(GroupsPageContext)!;
  const isConnected = group.allow_follow && group.is_active;
  const { t } = useTypedTranslation(LanguageNameSpaces.GROUPS);
  const { t: tBilling } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const workspace = useSelector(selectWorkspace);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const hideCreateButton =
    !isConnected || group.is_personal || isAdminWorkspace;

  const topSection = (
    <GroupMainInfo
      isOnboarding={true}
      name={group.name}
      image={group.image}
      stars={group.extra_info?.avg_stars}
      numReviewers={group.extra_info?.num_of_reviewers}
      numSubscribers={group.extra_info?.number_of_subscribers}
      groupId={group.id}
      hideCreateButton={hideCreateButton}
      groupData={group}
      isMyGroups={true}
    />
  );

  const getGroupTypeTag = () => {
    if (isGroupsPage) {
      let icon: ReactNode;
      if (group.platform_type === PlatformType.TELEGRAM)
        icon = (
          <Telegram
            fillColor={theme.colors.buttonText}
            style={{ width: 12, marginRight: 2 }}
          />
        );
      else if (group.platform_type === PlatformType.DISCORD)
        icon = (
          <Discord fillColor={theme.colors.buttonText} style={{ width: 12 }} />
        );
      else if (group.platform_type === PlatformType.CORNIX)
        icon = (
          <CrowLogo fillColor={theme.colors.buttonText} style={{ width: 12 }} />
        );
      const content = (
        <Flex $justifyContent="start">
          {theme.isNewLook ? (
            <StatusDot $isConnected={isConnected} aria-hidden={true} />
          ) : (
            <PlatformIconWrapper $isConnected={isConnected}>
              {icon}
            </PlatformIconWrapper>
          )}
          <Title
            level={5}
            style={{ marginLeft: 8 }}
            type={isConnected ? undefined : "secondary"}
          >
            {isConnected
              ? t("my_group.connected")
              : t("my_group.not_connected")}
          </Title>
        </Flex>
      );
      if (!isConnected) {
        return (
          <Tooltip
            title={
              group.is_active
                ? INVALID_GROUP_AUTO_TRADING_WARNING
                : getInactiveGroupAutoTradingWarning(isWhitelabel)
            }
            showMobileBottomDrawer={true}
            mobileTitle={t("my_group.not_connected_mobile_title")}
          >
            {content}
          </Tooltip>
        );
      }
      return content;
    } else {
      return (
        <Flex $justifyContent="start">
          {group.extra_info?.is_free ? <PublicIcon /> : <PrivateIcon />}
          <Title level={5} style={{ marginLeft: 8 }}>
            {group.extra_info?.is_free
              ? t("my_group.public_group")
              : t("my_group.private_group")}
          </Title>
        </Flex>
      );
    }
  };

  const actions = (group.is_personal || isAdminWorkspace) && (
    <MyGroupActions group={group} />
  );

  return (
    <StyledCard
      onClick={onCardClick}
      $isAutoSize={isGroupsPage}
      $isGroupsPage={isGroupsPage}
      data-testid={GROUP_CARD_ID}
    >
      <Flex $alignItems="flex-start">
        {topSection}
        {actions}
      </Flex>
      {!group.is_personal && (
        <>
          <Border />
          <Flex>
            {getGroupTypeTag()}
            <Text type="secondary">
              {getGroupSubscriptionExpiration(group, tBilling)}
            </Text>
          </Flex>
        </>
      )}
    </StyledCard>
  );
};

export default MyGroupCard;

const StyledCard = styled(Card)<{
  $isAutoSize?: boolean;
  $isGroupsPage?: boolean;
}>`
  height: ${ONBOARDING_MY_GROUP_CARD_HEIGHT}px;
  width: ${ONBOARDING_GROUP_CARD_WIDTH}px;
  @media (max-width: ${MOBILE_VIRTUAL_LIST}) {
    width: ${MOBILE_ONBOARDING_GROUP_CARD_WIDTH}px;
  }
  ${(p) =>
    p.$isAutoSize &&
    `
    width: 100% !important;
    height: auto;
  `};
  ${(p) =>
    !p.$isGroupsPage &&
    `
      cursor: pointer;
    `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      box-shadow: ${getPanelShadow(p.theme)};
      transition: box-shadow 150ms, transform 150ms;
      ${p.$isAutoSize && "padding: 20px 24px;"}
      &:hover {
        box-shadow: inset 0 0 0 1px ${p.theme.colors.cardBorderColor},
          0 4px 12px rgba(16, 24, 40, 0.08);
        transform: translateY(-1px);
      }
    `}
`;
const Border = styled.div`
  border-top: ${(p) => p.theme.borders.light};
  padding-top: 16px;
  margin-top: 16px;
`;
// New look: connection is a status, so a small dot in its meaning colour
// rather than a filled platform badge.
const StatusDot = styled.span<{ $isConnected?: boolean }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) =>
    p.$isConnected ? p.theme.colors.green : p.theme.colors.lightestText};
`;
const PlatformIconWrapper = styled.div<{ $isConnected?: boolean }>`
  background-color: ${(p) =>
    p.$isConnected ? p.theme.colors.primaryBlue : p.theme.colors.secondaryGrey};
  border-radius: 10px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
